import {
  AlertTriangle,
  CalendarClock,
  FileWarning,
  MessageSquareDashed,
  Paperclip,
  ShieldAlert,
} from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  groupByState,
  shortDate,
  waitedFor,
  STATE_TONES,
  type ContentRow,
} from '../../utils/marketing'

/**
 * The content board, grouped by state.
 *
 * Rows, not cards in columns. Six states will not fit as columns on the 430px
 * screen three of the six people in this business work from, and a horizontal
 * scroll on a phone is how a board stops being read. The task board made the
 * same call for the same reason.
 *
 * Every badge on a row is a fact the database decided. Nothing here computes
 * whether something is late.
 */
export function ContentBoard({
  rows,
  selectedId,
  onOpen,
}: {
  rows: ContentRow[]
  selectedId: string | null
  onOpen: (id: string) => void
}) {
  const groups = groupByState(rows)

  return (
    <div className="divide-y divide-border">
      {groups.map((g) => (
        <section key={g.state}>
          <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-muted/60 px-4 py-2 backdrop-blur">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-foreground">
              {g.heading}
            </h2>
            <Chip tone={STATE_TONES[g.state]}>{g.rows.length}</Chip>
          </header>

          <ul>
            {g.rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => onOpen(row.id)}
                  aria-current={selectedId === row.id ? 'true' : undefined}
                  className={cn(
                    'flex w-full flex-col gap-1.5 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-accent/50',
                    selectedId === row.id && 'bg-[hsl(var(--brand-soft))]/40',
                  )}
                >
                  <div className="flex min-w-0 items-start gap-2">
                    {/* The post number in the month when it has one, because a
                        plan is a running order and "POST 07" is how the team
                        already refers to it. The internal ref is the fallback. */}
                    <span className="mt-0.5 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {row.post_no !== null ? `${String(row.post_no).padStart(2, '0')}` : `#${row.ref}`}
                    </span>
                    {/* Wraps rather than truncating. A third of the titles on the
                        task board run past one line and cutting them off is how a
                        list becomes unreadable at 430px. */}
                    <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
                      {row.title}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* Audience first among the labels. The monthly mix between
                        audiences is what a content plan is actually steering, so
                        it reads before the channel. */}
                    <Chip tone={row.audience === 'investor' ? 'brand' : 'neutral'}>
                      {row.audience_label ?? row.audience}
                    </Chip>
                    <Chip tone="neutral">{row.channel_label ?? row.channel}</Chip>
                    <Chip tone="neutral">{row.format_label ?? row.format}</Chip>

                    {row.plan_title && (
                      <span className="text-[11px] text-muted-foreground">{row.plan_title}</span>
                    )}

                    {/* Waiting, and for how long. The only queue in this domain,
                        so it is measured on the row rather than left to be
                        worked out from a date. */}
                    {row.awaiting_approval && row.days_waiting !== null && (
                      <Chip tone={row.days_waiting >= 3 ? 'red' : 'amber'}>
                        waiting {waitedFor(row.days_waiting)}
                      </Chip>
                    )}

                    {/* Signed off, dated, the date has gone, nothing recorded as
                        published. This is the failure the tab exists to make
                        visible: work that was approved and then quietly did not
                        go out. */}
                    {row.approved_and_overdue && (
                      <Chip tone="red" icon={<CalendarClock className="h-3 w-3" />}>
                        approved, date passed
                      </Chip>
                    )}

                    {row.submitted_with_no_file && (
                      <Chip tone="amber" icon={<FileWarning className="h-3 w-3" />}>
                        no file attached
                      </Chip>
                    )}

                    {row.asset_count > 0 && (
                      <Chip tone="neutral" icon={<Paperclip className="h-3 w-3" />}>
                        {row.asset_count}
                      </Chip>
                    )}

                    {/* Options written and none picked. The commonest way a
                        submission wastes a round, and the database refuses to
                        submit in this state. */}
                    {row.caption_not_chosen && (
                      <Chip tone="amber" icon={<MessageSquareDashed className="h-3 w-3" />}>
                        {row.caption_option_count} captions, none chosen
                      </Chip>
                    )}

                    {/* Not an error, and coloured accordingly. The plan's own
                        production notes say to look twice at this caption. */}
                    {row.guardrail_flag_count > 0 && (
                      <Chip tone="amber" icon={<ShieldAlert className="h-3 w-3" />}>
                        {row.guardrail_flag_count} to check
                      </Chip>
                    )}

                    {/* Rounds, not just the last decision. Three trips through
                        review is the number worth seeing, and it is invisible if
                        only the current state is shown. */}
                    {row.changes_requested_count > 0 && (
                      <Chip tone="amber" icon={<AlertTriangle className="h-3 w-3" />}>
                        {row.changes_requested_count} sent back
                      </Chip>
                    )}

                    <span className="ml-auto text-[11px] text-muted-foreground">
                      {row.state === 'Published'
                        ? `published ${shortDate(row.published_at)}`
                        : row.scheduled_for
                          ? `for ${shortDate(row.scheduled_for)}`
                          : 'no date set'}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}
